import React, { useContext, useCallback, Fragment, useState } from 'react';
import CbmContext from '../../Context/Cbm/cbmContext';
import './NonMnMileage.css';
import Comment from '../Comment/Comment';
import EmployeeInfo from '../EmployeeInfo/EmployeeInfo';
import Store from '../Store/Store';
import { useFormSetup } from '../../hooks/useFormSetup';
import ValidationErrors from '../Utils/ValidationErrors';
import { findStoreById } from '../Utils/helperFuncs';

const NonMnMileage = ({ setPayLoad, setFormError, errMsg }) => {
  const cbmContext = useContext(CbmContext);
  const { stores, portalUser, submittedData, errorCode } = cbmContext;

  const [mileageData, setMileageData] = useState({
    employee: {
      employeeNumber: '',
      employeeFirst: '',
      employeeMiddle: '',
      employeeLast: '',
      employeeSecondLast: '',
    },
    mileageDates: [
      {
        date: '',
        location1: {},
        location2: {},
        rt_ow: '',
      },
    ],
    comments: '',
    portalUser,
  });

  // Use unified form setup hook
  const {
    validationErrors,
    getArrayFieldError,
    hasArrayFieldError,
    clearArrayItemErrors,
    getFieldError,
  } = useFormSetup({
    formName: 'nonMnMileage',
    formData: mileageData,
    setFormData: setMileageData,
    setPayLoad,
    setFormError,
    submittedData,
    errorCode,
  });

  const changeEmpInfo = useCallback(
    (e) => {
      const { name, value } = e.target;
      setMileageData((prev) => ({
        ...prev,
        employee: { ...prev.employee, [name]: value },
      }));
    },
    [setMileageData]
  );

  const updateMileages = useCallback(
    (e, index) => {
      const { name, value, id } = e.target;
      setMileageData((prev) => {
        const mileageTemp = [...prev.mileageDates];
        if (name === 'location1' || name === 'location2') {
          const foundStore = findStoreById(stores, parseInt(value));
          mileageTemp[index][name] = foundStore;
        } else if (id === 'rt' || id === 'ow') {
          const radioOption = name.split('_').slice(0, 2).join('_');
          mileageTemp[index][radioOption] = value;
        } else {
          mileageTemp[index][name] = value;
        }
        return { ...prev, mileageDates: mileageTemp };
      });
    },
    [stores, setMileageData]
  );

  const addMileage = useCallback(() => {
    setMileageData((prev) => ({
      ...prev,
      mileageDates: [
        ...prev.mileageDates,
        { date: '', location1: '', location2: '', rt_ow: '' },
      ],
    }));
  }, [setMileageData]);

  const removeMileage = useCallback(
    (index) => {
      clearArrayItemErrors('mileageDates', index);
      setMileageData((prev) => ({
        ...prev,
        mileageDates: prev.mileageDates.filter((_, i) => i !== index),
      }));
    },
    [clearArrayItemErrors, setMileageData]
  );

  const setMileageComments = useCallback(
    (e) => {
      const { value } = e.target;
      setMileageData((prev) => ({ ...prev, comments: value }));
    },
    [setMileageData]
  );

  return (
    <Fragment>
      <div className="heading">
        <h1>Non MN Mileage</h1>
      </div>
      {errMsg && (
        <div className="error-message-container">
          <p className="error-message">{errMsg}</p>
          <ValidationErrors errors={validationErrors} />
        </div>
      )}
      <fieldset>
        <legend>Employee Info</legend>
        <EmployeeInfo
          payload={mileageData}
          changeEmpInfo={changeEmpInfo}
          getFieldError={getFieldError}
          errMsg={errMsg}
        />
      </fieldset>
      <fieldset className="mileageDates">
        <legend>Mileage date</legend>
        {mileageData.mileageDates.map((mileageDate, index) => {
          return (
            <Fragment key={'mileage_' + index}>
              <div
                id={'mileageDateId'}
                className="nonmn-mileage-date-container"
              >
                <div
                  className={
                    errMsg && hasArrayFieldError('mileageDates', index, 'date')
                      ? 'mileageDate error'
                      : 'nonmn-mileageDate'
                  }
                >
                  <div>
                    <label
                      htmlFor={`mileageDate_${index}`}
                      className="required"
                    >
                      Date
                    </label>
                    <input
                      type="date"
                      name="date"
                      id={`mileageDate_${index}`}
                      value={mileageDate.date || ''}
                      onChange={(e) => updateMileages(e, index)}
                    />
                    {errMsg &&
                      getArrayFieldError('mileageDates', index, 'date') && (
                        <span className="error-text">
                          {getArrayFieldError('mileageDates', index, 'date')}
                        </span>
                      )}
                  </div>
                </div>
                <div className="nonmn-mileage-location">
                  <Store
                    storeId={'location_' + index}
                    storeValue={mileageData.mileageDates[index].location1?.id}
                    storeList={stores}
                    header="location 1"
                    changeStoreInfo={updateMileages}
                    index={index}
                    elementName="location1"
                  />
                  {errMsg &&
                    getArrayFieldError(
                      'mileageDates',
                      index,
                      'location1.id'
                    ) && (
                      <span className="error-text">
                        {getArrayFieldError(
                          'mileageDates',
                          index,
                          'location1.id'
                        )}
                      </span>
                    )}
                </div>
                <div className="nonmn-mileage-location">
                  <Store
                    storeId={'location2_' + index}
                    storeValue={mileageData.mileageDates[index].location2?.id}
                    storeList={stores}
                    header="Location 2"
                    changeStoreInfo={updateMileages}
                    index={index}
                    elementName="location2"
                  />
                  {errMsg &&
                    getArrayFieldError(
                      'mileageDates',
                      index,
                      'location2.id'
                    ) && (
                      <span className="error-text">
                        {getArrayFieldError(
                          'mileageDates',
                          index,
                          'location2.id'
                        )}
                      </span>
                    )}
                </div>
                <div className="nonmn-rt-ow">
                  <label className="required">Select RT/OW:</label>
                  <div>
                    Round trip
                    <input
                      type="radio"
                      checked={mileageDate.rt_ow === 'RT'}
                      id="rt"
                      name={'rt_ow_' + index}
                      value="RT"
                      onChange={(e) => updateMileages(e, index)}
                    />
                  </div>
                  <div>
                    One way
                    <input
                      type="radio"
                      checked={mileageDate.rt_ow === 'OW'}
                      id="ow"
                      name={'rt_ow_' + index}
                      value="OW"
                      onChange={(e) => updateMileages(e, index)}
                    />
                  </div>
                </div>
              </div>
              <div className="mileage-add-remove-btns">
                <div className="add">
                  <button onClick={() => addMileage()}>Add mileage</button>
                </div>
                <div className="remove">
                  <button onClick={() => removeMileage(index)}>
                    {' '}
                    Remove mileage
                  </button>
                </div>
              </div>
            </Fragment>
          );
        })}
      </fieldset>
      <fieldset className="mileageComments">
        <Comment
          commentsId={'mileageCommentsId'}
          payload={mileageData.comments}
          onHandleChange={setMileageComments}
        />
      </fieldset>
    </Fragment>
  );
};
// eslint-disable-next-line
NonMnMileage.propTypes = {};

export default NonMnMileage;
